import { useMemo, useState } from 'react';
import type { AppRecord } from '../../lib/types';
import type { CompassLocale } from '../i18n';
import { buildCompassHref } from '../i18n';
import { deriveAppCategory, promptExcerpt } from '../lib/catalogPresentation';

function PromptCard(props: { app: AppRecord; index: number; text: Record<string, string>; locale: CompassLocale }): React.JSX.Element {
  const { app, index, text, locale } = props;
  const [showZh, setShowZh] = useState(false);
  const hasZh = Boolean(app.promptTranslatedZh);
  const displayText = showZh && hasZh ? (app.promptTranslatedZh ?? app.promptText) : app.promptText;

  return (
    <article
      key={app.appId}
      className={`glass-card rounded-2xl p-6 flex flex-col gap-4 hover:scale-[1.02] transition-transform duration-300 ${index === 1 ? 'lg:col-span-2' : ''}`}
    >
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2">
          <span className="text-cyan text-xs uppercase tracking-widest">{deriveAppCategory(app)}</span>
          {hasZh && (
            <div className="flex items-center gap-0.5 p-0.5 bg-white/5 border border-white/10 rounded-full">
              <button
                className={`px-3 py-1 rounded-full font-geist font-bold text-[10px] uppercase tracking-widest border-none cursor-pointer transition-all ${!showZh ? 'bg-[#00ffff] text-[#030712]' : 'bg-transparent text-on-surface-variant hover:text-[#00ffff]'}`}
                onClick={(e) => { e.stopPropagation(); setShowZh(false); }}
                type="button"
              >
                EN
              </button>
              <button
                className={`px-3 py-1 rounded-full font-geist font-bold text-[10px] uppercase tracking-widest border-none cursor-pointer transition-all ${showZh ? 'bg-[#00ffff] text-[#030712]' : 'bg-transparent text-on-surface-variant hover:text-[#00ffff]'}`}
                onClick={(e) => { e.stopPropagation(); setShowZh(true); }}
                type="button"
              >
                中文
              </button>
            </div>
          )}
        </div>
        <span className="rounded-full bg-surface-container-highest px-3 py-1 text-xs text-on-surface">{app.appId}</span>
      </div>
      <h3 className="font-geist text-2xl font-semibold text-on-surface">{app.name}</h3>
      <p className="text-sm leading-relaxed text-on-surface-variant">{app.summary}</p>
      <div className="rounded-xl bg-surface-container p-4 relative group">
        <p className="font-mono text-sm leading-relaxed text-outline-variant whitespace-pre-wrap break-words">
          {promptExcerpt(displayText, index === 1 ? 420 : 220)}
        </p>
        <button
          className="absolute top-3 right-3 rounded bg-surface px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => navigator.clipboard.writeText(displayText)}
          type="button"
        >
          <span className="material-symbols-outlined text-sm text-on-surface">content_copy</span>
        </button>
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-outline-variant/20 pt-4 mt-auto">
        <a
          className="text-primary hover:text-secondary text-xs uppercase tracking-widest flex items-center gap-1 no-underline transition-colors"
          href={buildCompassHref(`/apps/${app.appId}`, locale)}
        >
          {text.view}
          <span className="material-symbols-outlined text-sm">arrow_forward</span>
        </a>
        <button
          className="btn-secondary-cyan px-4 py-2 rounded-full text-xs uppercase tracking-widest"
          onClick={() => navigator.clipboard.writeText(displayText)}
          type="button"
        >
          {text.copy}
        </button>
      </div>
    </article>
  );
}

export function PromptLibrary(props: {
  apps: AppRecord[];
  locale: CompassLocale;
}): React.JSX.Element {
  const { apps, locale } = props;
  const text = locale === 'zh-CN'
    ? {
        title: 'Prompt 案例库',
        description: '汇集可复用的 prompt 结构、生成约束和界面产出线索，方便复盘与二次创作。',
        search: '搜索 prompt、应用或关键词...',
        all: '全部',
        copy: '复制 Prompt',
        view: '查看结果',
      }
    : {
        title: 'Prompt Library',
        description: 'A reusable library of prompt structures, output constraints, and interface-generation patterns.',
        search: 'Search prompts, apps, or keywords...',
        all: 'All',
        copy: 'Copy Prompt',
        view: 'View Result',
      };

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(text.all);
  const categories = useMemo(() => [text.all, ...Array.from(new Set(apps.map((app) => deriveAppCategory(app))))], [apps, text.all]);

  const filteredApps = useMemo(() => {
    const query = search.trim().toLowerCase();
    return apps.filter((app) => {
      const appCategory = deriveAppCategory(app);
      if (category !== text.all && category !== appCategory) return false;
      if (!query) return true;
      return [app.name, app.summary, app.promptText, app.promptTranslatedZh, appCategory].filter(Boolean).join(' ').toLowerCase().includes(query);
    });
  }, [apps, category, search, text.all]);

  return (
    <main className="flex-grow max-w-[1280px] mx-auto w-full px-4 md:px-10 py-12 pt-[100px] flex flex-col gap-12">
      <header className="text-center md:text-left">
        <h1 className="font-geist text-[48px] leading-[1.1] font-bold text-on-surface mb-4">{text.title}</h1>
        <p className="text-lg leading-relaxed text-on-surface-variant max-w-2xl">{text.description}</p>
      </header>

      <section className="glass-card rounded-2xl p-6 flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
        <label className="relative w-full lg:max-w-md">
          <span className="material-symbols-outlined absolute left-3 top-3 text-outline">search</span>
          <input
            className="w-full rounded-full border border-outline-variant/30 bg-surface-container-highest py-3 pl-10 pr-4 text-on-surface outline-none placeholder:text-outline-variant"
            placeholder={text.search}
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <div className="scrollbar-hidden overflow-x-auto pb-1">
          <div className="flex w-max gap-3 pr-2">
            {categories.map((item) => {
              const active = item === category;
              return (
                <button
                  key={item}
                  className={active
                    ? 'bg-primary-container px-4 py-2 rounded-full text-xs uppercase tracking-widest text-on-primary whitespace-nowrap'
                    : 'border border-outline-variant/40 px-4 py-2 rounded-full text-xs uppercase tracking-widest text-on-surface-variant hover:text-on-surface transition-colors whitespace-nowrap'}
                  onClick={() => setCategory(item)}
                  type="button"
                >
                  {item}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredApps.map((app, index) => (
          <PromptCard key={app.appId} app={app} index={index} text={text} locale={locale} />
        ))}
      </section>
    </main>
  );
}
